import React from 'react'
import { Link } from 'react-router-dom'
import StaticCmsPage, { StaticSection } from '../../components/nemonto/StaticCmsPage'

// The terms text on its own - also shown in a modal from the signup
// consent line (LegalModal).
export const TermsOfUseContent = () => (
    <>
        <StaticSection>
            <p><strong>Last updated: November 3, 2026</strong></p>
            <p>
                Welcome to Nemonto. These Terms of Use ("Terms") are an agreement between you and Nemonto ("Nemonto",
                "we", "us" or "our") and govern your use of the Nemonto website at{' '}
                <a href="https://nemonto.com/" target="_blank" rel="noopener noreferrer">nemonto.com</a>, the Nemonto
                mobile apps, and related services (together, the "Service").
            </p>
            <p>
                By creating an account or using the Service, you agree to these Terms and to our{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>. If you don't agree, please don't use the Service.
            </p>
        </StaticSection>

        <StaticSection heading="1. Who can use Nemonto">
            <ul>
                <li>You must be at least 13 years old. If you are under the age of majority where you live, you may only use the Service with the permission of a parent or legal guardian.</li>
                <li>You must not have been previously banned from the Service, and you must be able to form a binding agreement with us.</li>
                <li>If you use Nemonto on behalf of a business, you confirm you have authority to accept these Terms for that business.</li>
            </ul>
        </StaticSection>

        <StaticSection heading="2. Your account">
            <ul>
                <li>Give accurate information when you sign up, and keep it up to date.</li>
                <li>Keep your password and one-time verification codes private. You're responsible for activity on your account.</li>
                <li>Don't impersonate anyone, or create an account for someone else without their permission.</li>
                <li>Tell us right away through our <Link to="/contact-us">Contact Us</Link> page if you think your account has been accessed without permission.</li>
                <li>You can delete your account at any time from the app's settings.</li>
            </ul>
        </StaticSection>

        <StaticSection heading="3. Your content">
            <p>
                "Content" means anything you post on Nemonto, including recipes, posts, stories, photos, videos,
                "Made it" entries, challenge entries and challenges, comments, messages and ad posts.
            </p>
            <ul>
                <li><strong>You own your content.</strong> We don't claim ownership of what you post.</li>
                <li>
                    <strong>You give us a licence to run the Service.</strong> By posting content, you grant Nemonto a
                    worldwide, non-exclusive, royalty-free, transferable and sublicensable licence to host, store, copy,
                    display, distribute, adapt (for example, resize or format) and promote your content in connection with
                    operating, improving and marketing the Service. This licence ends when you delete the content or your
                    account, except where it has been shared with others who haven't deleted it, or where we need to keep
                    it for legal reasons.
                </li>
                <li><strong>You're responsible for what you post.</strong> You confirm you have the rights to everything you post, including any music, images or video, and that it doesn't break these Terms or the law.</li>
                <li><strong>Recipes and cooking.</strong> Recipes, nutrition information (such as calories) and cooking tips are shared by users and are for general information only. Nemonto doesn't check them for accuracy, safety, allergens or dietary suitability. Always use your own judgement in the kitchen, especially with allergies, food safety and special diets.</li>
            </ul>
        </StaticSection>

        <StaticSection heading="4. Community rules">
            <p>Nemonto is a shared table. When you use the Service, you agree not to:</p>
            <ul>
                <li>Post content that is illegal, hateful, harassing, threatening, violent, sexually explicit, or that bullies or discriminates against anyone.</li>
                <li>Post content that infringes someone else's copyright, trademark, privacy or other rights, including copying someone else's recipe, photos or video and passing it off as your own.</li>
                <li>Share other people's personal information without their permission.</li>
                <li>Post spam, misleading content, scams, or unauthorised advertising outside of Nemonto ad posts.</li>
                <li>Promote dangerous practices, including unsafe food handling presented as safe.</li>
                <li>Manipulate likes, followers, "Made it" counts or challenge results, including by using fake accounts or bots.</li>
                <li>Upload viruses or malicious code, scrape the Service, or try to access accounts, systems or data you're not authorised to access.</li>
                <li>Interfere with or disrupt the Service or other people's use of it.</li>
            </ul>
            <p>
                You can block users and report content you think breaks these rules. We may remove content or restrict
                accounts that we believe violate these Terms.
            </p>
        </StaticSection>

        <StaticSection heading="5. Challenges">
            <ul>
                <li>Challenges (including Nemonto Challenges and Shape Challenges) are hosted by creators, who set the rules, such as required ingredients, cuisine, maximum cook time and how long entries are open.</li>
                <li>Your entry must follow the challenge rules and these Terms, and must be your own dish and your own photos or video.</li>
                <li>The host picks the winner. Their decision is final, and Nemonto isn't responsible for how a host judges a challenge.</li>
                <li>Winners receive a badge on their profile. Unless a challenge clearly states otherwise in writing, challenges don't include cash or other prizes. If a challenge offers a prize, the host is responsible for it and for following any laws that apply.</li>
                <li>We may remove entries, disqualify participants or end a challenge if we see cheating, abuse or a breach of these Terms.</li>
            </ul>
        </StaticSection>

        <StaticSection heading="6. Ad posts and payments">
            <ul>
                <li>You can create ad posts to promote your content, products or services. Ads must be truthful, follow these Terms and comply with all laws that apply to advertising.</li>
                <li>Payments are processed by our payment provider, Stripe, under its own terms. You confirm you're authorised to use the payment method you provide.</li>
                <li>Prices and ad run times are shown before you pay. Unless required by law, payments are non-refundable once an ad has started running.</li>
                <li>We may reject, pause or remove any ad that breaks these Terms. If we remove an ad that hasn't run because it breaks our rules, we're not required to refund it.</li>
                <li>Some features, such as Pro features, may be offered for a fee in the future. We'll show the price and terms before you're charged.</li>
            </ul>
        </StaticSection>

        <StaticSection heading="7. Our content and intellectual property">
            <p>
                The Service, including the Nemonto name, logo, design, software and the content we create, belongs to
                Nemonto and its licensors and is protected by law. You may not copy, modify, distribute, sell or
                reverse engineer any part of the Service, or use our name or logo, without our written permission.
            </p>
        </StaticSection>

        <StaticSection heading="8. Copyright complaints">
            <p>
                We respect intellectual property rights. If you believe content on Nemonto infringes your copyright,
                please send us a notice through our <Link to="/contact-us">Contact Us</Link> page that includes: your
                contact details; a description of the copyrighted work; the link to the content you believe infringes it;
                a statement that you have a good-faith belief the use isn't authorised; and a statement, under penalty of
                perjury, that your notice is accurate and that you're the owner or authorised to act for the owner. We may
                remove the content and, for repeat infringers, close their accounts.
            </p>
        </StaticSection>

        <StaticSection heading="9. Third-party links and services">
            <p>
                The Service may include links to, or features from, third-party websites and services, including in user
                posts and ads. We don't control them and aren't responsible for their content, products or practices.
            </p>
        </StaticSection>

        <StaticSection heading="10. Suspension and termination">
            <p>
                You can stop using Nemonto and delete your account at any time. We may suspend or end your access,
                or remove your content, if you break these Terms, if we have to by law, or to protect our users or the
                Service. Sections that by their nature should continue after termination (such as ownership,
                disclaimers, limitation of liability and disputes) will continue to apply.
            </p>
        </StaticSection>

        <StaticSection heading="11. Disclaimers">
            <p>
                The Service is provided "as is" and "as available". To the fullest extent permitted by law, Nemonto
                makes no warranties of any kind, express or implied, including warranties of merchantability, fitness
                for a particular purpose and non-infringement. We don't guarantee that the Service will be uninterrupted,
                error-free or secure, or that any content (including recipes and nutrition information) is accurate,
                safe or reliable. Content posted by users reflects their views, not ours.
            </p>
        </StaticSection>

        <StaticSection heading="12. Limitation of liability">
            <p>
                To the fullest extent permitted by law, Nemonto and its partners, officers, employees and service
                providers won't be liable for any indirect, incidental, special, consequential or punitive damages, or
                for any loss of profits, data or goodwill, arising from your use of, or inability to use, the Service,
                including any injury, illness or damage resulting from preparing or eating food based on content found on
                the Service. Our total liability for any claim relating to the Service is limited to the greater of the
                amount you paid us in the 12 months before the claim or USD $100.
            </p>
        </StaticSection>

        <StaticSection heading="13. Indemnity">
            <p>
                You agree to defend and hold harmless Nemonto and its partners from any claims, damages and costs
                (including reasonable legal fees) arising from your content, your use of the Service, or your breach of
                these Terms.
            </p>
        </StaticSection>

        <StaticSection heading="14. Governing law and disputes">
            <p>
                These Terms are governed by the laws of the Commonwealth of Virginia, United States, without regard to its
                conflict-of-law rules. Before filing a claim, please <Link to="/contact-us">contact us</Link> so we can try to
                resolve it informally. Any dispute that can't be resolved informally will be brought exclusively in the
                state or federal courts located in Virginia, and you and Nemonto consent to their jurisdiction. Nothing in
                these Terms limits rights you have under consumer protection laws that can't be waived.
            </p>
        </StaticSection>

        <StaticSection heading="15. Changes to these Terms">
            <p>
                We may update these Terms from time to time. When we do, we'll change the "Last updated" date above, and
                for significant changes we'll let you know through the Service or by email. If you keep using Nemonto after
                the changes take effect, you accept the updated Terms.
            </p>
        </StaticSection>

        <StaticSection heading="16. General">
            <p>
                These Terms, together with our <Link to="/privacy-policy">Privacy Policy</Link>, are the entire agreement
                between you and Nemonto about the Service. If any part of these Terms is found unenforceable, the rest stays
                in effect. If we don't enforce a part of these Terms, that doesn't mean we give up our right to do so later.
                You may not transfer these Terms without our consent; we may transfer them as part of a business transfer.
            </p>
        </StaticSection>

        <StaticSection heading="17. Contact us">
            <p>
                Questions about these Terms? Reach us through our <Link to="/contact-us">Contact Us</Link> page.
            </p>
        </StaticSection>
    </>
)

const TermsofService = () => (
    <StaticCmsPage slug="terms-of-use" title="Terms of Use" subtitle="The rules for using Nemonto.">
        <TermsOfUseContent />
    </StaticCmsPage>
)

export default TermsofService
